import React, { useState, useRef, useEffect } from "react";
import styles from "./Header.module.css";
import { FiBell, FiSettings } from "react-icons/fi";
import {
  RiLogoutBoxRLine,
  RiArrowDownSLine,
  RiPaletteLine,
  RiGlobalLine,
  RiCouponLine,
} from "react-icons/ri";
import greenRockLogo from "../../assets/images/green_rock.png";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { COLORS } from "../../constants/constants";
import { IColor } from "../../interfaces/models";
import { useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

const Header: React.FC = () => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authData = useSelector((state: RootState) => state.auth);
  const accountRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        accountRef.current &&
        !accountRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target as Node)
      ) {
        setIsSettingsOpen(false);
      }
    }
    if (isOptionsOpen || isSettingsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOptionsOpen, isSettingsOpen]);

  const handleSignOut = () => {
    setShowSignOutModal(true);
    setIsOptionsOpen(false);
  };

  const handleConfirmSignOut = () => {
    dispatch(logout());
    navigate("/login");
    setShowSignOutModal(false);
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  const handleManageColors = () => {
    navigate("/manage-colors");
    setIsSettingsOpen(false);
  };

  const handleColorChange = (index: number, hex: string) => {
    // This function is no longer used in the new implementation
  };

  const handleManageCountries = () => {
    navigate("/manage-countries");
    setIsSettingsOpen(false);
  };

  const handleManageDiscount = () => {
    navigate("/manage-discount");
    setIsSettingsOpen(false);
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.left}>
          <img
            src={greenRockLogo}
            alt="Green Rock Logo"
            className={styles.logo}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/")}
            tabIndex={0}
            role="button"
            aria-label="Go to home page"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") navigate("/");
            }}
          />
        </div>
        <div className={styles.right}>
          <div className={styles.iconContainer} ref={settingsRef}>
            <button className={styles.iconButton} onClick={handleSettingsClick}>
              <FiSettings className={styles.icon} />
            </button>
            {isSettingsOpen && (
              <div className={styles.settingsDropdown}>
                <button
                  className={styles.optionItem}
                  onClick={handleManageColors}
                >
                  <RiPaletteLine className={styles.optionIcon} />
                  Manage Colors
                </button>
                <button
                  className={styles.optionItem}
                  onClick={handleManageCountries}
                >
                  <RiGlobalLine className={styles.optionIcon} />
                  Manage Countries
                </button>
                <button
                  className={styles.optionItem}
                  onClick={handleManageDiscount}
                >
                  <RiCouponLine className={styles.optionIcon} />
                  Manage Discount
                </button>
              </div>
            )}
          </div>
          <div className={styles.iconContainer}>
            <button className={styles.iconButton}>
              <FiBell className={styles.icon} />
            </button>
          </div>

          <div className={styles.accountSection} ref={accountRef}>
            <button
              className={styles.accountButton}
              onClick={() => setIsOptionsOpen(!isOptionsOpen)}
            >
              <span className={styles.userSubtext}>
                {(authData as any)?.data?.userData?.name ||
                  (authData as any)?.userData?.name ||
                  "-"}
              </span>
              <RiArrowDownSLine className={styles.chevron} />
            </button>

            {isOptionsOpen && (
              <div className={styles.optionsDropdown}>
                <div className={styles.optionsSection}>
                  <button className={styles.optionItem} onClick={handleSignOut}>
                    <RiLogoutBoxRLine className={styles.optionIcon} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      <ConfirmationModal
        isOpen={showSignOutModal}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        onConfirm={handleConfirmSignOut}
        onCancel={() => setShowSignOutModal(false)}
        type="warning"
      />
    </>
  );
};

export default Header;
